import { ChevronLeft, ChevronRight } from 'lucide-react';
import { RowsPerPageSelect } from '@/components/ui/RowsPerPageSelect';

interface RecordedClassesPaginationProps {
  totalItems: number;
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}

export function RecordedClassesPagination({
  totalItems,
  currentPage,
  pageSize,
  onPageChange,
  onPageSizeChange,
}: RecordedClassesPaginationProps) {
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);

  const pages = Array.from({ length: totalPages }, (_, i) => i + 1);

  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 py-2 text-xs sm:text-sm text-ink-3">
      {/* Left: Range text */}
      <div className="font-medium text-xs sm:text-sm">
        Showing <span className="font-bold text-ink">{startItem}</span> to{' '}
        <span className="font-bold text-ink">{endItem}</span> of{' '}
        <span className="font-bold text-ink">{totalItems}</span> recordings
      </div>

      {/* Right: Rows per page + Page Buttons */}
      <div className="flex flex-wrap items-center justify-between sm:justify-end gap-3 w-full sm:w-auto">
        <RowsPerPageSelect
          value={pageSize}
          options={[6, 10, 20, 50]}
          onChange={onPageSizeChange}
        />

        {/* Page navigation */}
        <div className="flex items-center gap-1 overflow-x-auto no-scrollbar max-w-full">
          <button
            type="button"
            disabled={currentPage <= 1}
            onClick={() => onPageChange(currentPage - 1)}
            className="flex h-8 w-8 items-center justify-center rounded-[8px] border border-line bg-surface-2 text-ink-3 hover:text-ink disabled:opacity-40 disabled:pointer-events-none transition-colors cursor-pointer shrink-0"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>

          {pages.map((p) => {
            const isActive = p === currentPage;
            return (
              <button
                key={p}
                type="button"
                onClick={() => onPageChange(p)}
                className={`flex h-8 w-8 items-center justify-center rounded-[8px] text-xs font-bold transition-all cursor-pointer shrink-0 ${
                  isActive
                    ? 'bg-indigo-600 text-white shadow-xs'
                    : 'border border-line bg-surface-2 text-ink-2 hover:text-ink'
                }`}
              >
                {p}
              </button>
            );
          })}

          <button
            type="button"
            disabled={currentPage >= totalPages}
            onClick={() => onPageChange(currentPage + 1)}
            className="flex h-8 w-8 items-center justify-center rounded-[8px] border border-line bg-surface-2 text-ink-3 hover:text-ink disabled:opacity-40 disabled:pointer-events-none transition-colors cursor-pointer shrink-0"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
